import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import {
  askTrainingQuestion,
  buildStepPins,
  createTrainingConversation,
  fetchTrainerAvailable,
} from '../../../services/trainingAiService';
import TrainingMarkdown from './TrainingMarkdown';
import ReadAloudButton from './ReadAloudButton';

export default function TrainingStepPanel({
  open,
  onClose,
  lesson,
  moduleId,
  rolePath,
  currentStep,
  activeStep,
}) {
  const { tenantId } = useAuth();
  const [available, setAvailable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const conversationRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchTrainerAvailable().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    conversationRef.current = null;
    setMessages([]);
    setQuestion('');
    setError('');
  }, [lesson?.procedureId, activeStep?.id, open]);

  const ensureConversation = useCallback(async () => {
    if (conversationRef.current) return conversationRef.current;
    const pins = buildStepPins({
      procedureId: lesson?.procedureId,
      stepId: activeStep?.id,
      stepNumber: activeStep?.number,
      stepText: activeStep?.text,
      moduleId,
      rolePath,
      lessonTitle: lesson?.title,
    });
    const convo = await createTrainingConversation({ tenantId, pins });
    conversationRef.current = convo?.conversationId || convo?.id;
    return conversationRef.current;
  }, [activeStep, lesson, moduleId, rolePath, tenantId]);

  const handleAsk = useCallback(async () => {
    const text = question.trim();
    if (!text || loading) return;
    setLoading(true);
    setError('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setQuestion('');
    try {
      const conversationId = await ensureConversation();
      const pins = buildStepPins({
        procedureId: lesson?.procedureId,
        stepId: activeStep?.id,
        stepNumber: activeStep?.number,
        stepText: activeStep?.text,
        moduleId,
        rolePath,
        lessonTitle: lesson?.title,
      });
      const result = await askTrainingQuestion({
        conversationId,
        tenantId,
        message: text,
        pins,
      });
      const answer = result?.answer || result?.whatHappened || 'No answer returned.';
      const warning = result?.unsupportedWarning;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: answer, warning },
      ]);
    } catch (err) {
      setError(err?.message || 'Trainer Q&A is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [
    activeStep,
    ensureConversation,
    lesson,
    loading,
    moduleId,
    question,
    rolePath,
    tenantId,
  ]);

  return (
    <Collapse in={open}>
      <Paper sx={{ p: 2, mt: 2 }} data-testid="training-step-panel">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Ask about this step
          </Typography>
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        </Stack>

        {available === false ? (
          <Alert severity="info">
            Trainer Q&amp;A is temporarily unavailable. You can continue the lesson normally.
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}

        <Stack spacing={1.5} sx={{ mb: 2, maxHeight: 240, overflow: 'auto' }}>
          {messages.map((msg, idx) => (
            <Box
              key={`${msg.role}-${idx}`}
              sx={{
                p: 1.25,
                borderRadius: 1,
                bgcolor: msg.role === 'user' ? 'grey.100' : 'primary.50',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {msg.role === 'user' ? 'You' : 'Trainer'}
              </Typography>
              <TrainingMarkdown readAloud={msg.role === 'assistant'}>{msg.text}</TrainingMarkdown>
              {msg.warning ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box sx={{ flex: 1 }}>{msg.warning}</Box>
                    <ReadAloudButton text={msg.warning} label="Read warning aloud" />
                  </Stack>
                </Alert>
              ) : null}
            </Box>
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Why do I need a segment?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading || available === false}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
            data-testid="training-step-question"
          />
          <Button
            variant="contained"
            onClick={handleAsk}
            disabled={loading || !question.trim() || available === false}
            data-testid="training-step-ask"
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Ask'}
          </Button>
        </Stack>
      </Paper>
    </Collapse>
  );
}
